/**
 * Firebase Authentication Service
 *
 * Provides sign-in, sign-out, and token management functions
 * for Firebase Authentication.
 *
 * Key Features:
 * - Microsoft (Entra ID) sign-in via popup
 * - Email/Password sign-in
 * - Custom claims management
 * - Token refresh utilities
 */

import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signOut as firebaseSignOut,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { auth } from './firebaseConfig';
import { microsoftProvider } from './authProviders';

/**
 * Custom claims structure set by the backend API
 */
export interface UserClaims {
  role?: string;
  organisationId?: string;
  organisationSlug?: string;
}

/**
 * Sign in with Microsoft (Entra ID)
 *
 * Opens a popup window for Microsoft authentication.
 * This is the primary authentication method for enterprise users.
 *
 * @returns The authenticated Firebase user
 * @throws FirebaseError if authentication fails
 */
export async function signInWithMicrosoft(): Promise<User> {
  console.info('[AuthService] Starting Microsoft sign-in...');
  
  const result: UserCredential = await signInWithPopup(auth, microsoftProvider);
  
  console.info('[AuthService] Microsoft sign-in successful:', result.user.email);
  return result.user;
}

/**
 * Sign in with Email and Password
 *
 * Standard email/password authentication for users without
 * Microsoft accounts.
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns The authenticated Firebase user
 * @throws FirebaseError if authentication fails
 */
export async function signInWithEmailPassword(
  email: string,
  password: string
): Promise<User> {
  console.info('[AuthService] Starting email sign-in for:', email);
  
  const result: UserCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password
  );
  
  console.info('[AuthService] Email sign-in successful');
  return result.user;
}

/**
 * Create a new user with Email and Password
 *
 * Registers a new Firebase user. After registration, the user
 * must be provisioned in the backend to receive custom claims.
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns The newly created Firebase user
 * @throws FirebaseError if registration fails
 */
export async function registerWithEmailPassword(
  email: string,
  password: string
): Promise<User> {
  console.info('[AuthService] Starting email registration for:', email);
  
  const result: UserCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );
  
  console.info('[AuthService] Email registration successful');
  return result.user;
}

/**
 * Send a password reset email
 *
 * Sends an email to the specified address with a link to reset
 * their password. The email must be associated with an existing account.
 *
 * @param email - User's email address
 * @throws FirebaseError if the email is not found or invalid
 */
export async function sendPasswordResetEmail(email: string): Promise<void> {
  console.info('[AuthService] Sending password reset email to:', email);
  await firebaseSendPasswordResetEmail(auth, email);
  console.info('[AuthService] Password reset email sent');
}

/**
 * Sign out the current user
 *
 * Clears the Firebase authentication session.
 */
export async function signOut(): Promise<void> {
  console.info('[AuthService] Signing out...');
  await firebaseSignOut(auth);
  console.info('[AuthService] Sign-out complete');
}

/**
 * Get custom claims from the user's ID token
 *
 * Custom claims are set by the backend API and include:
 * - role: User's role within the organisation
 * - organisationId: UUID of the user's organisation
 * - organisationSlug: URL-safe slug of the organisation
 *
 * @param user - Firebase user object
 * @returns Custom claims object
 */
export async function getUserClaims(user: User): Promise<UserClaims> {
  const idTokenResult = await user.getIdTokenResult();
  
  return {
    role: idTokenResult.claims.role as string | undefined,
    organisationId: idTokenResult.claims.organisationId as string | undefined,
    organisationSlug: idTokenResult.claims.organisationSlug as string | undefined,
  };
}

/**
 * Force refresh the user's ID token
 *
 * This should be called after the backend sets custom claims
 * to ensure the frontend has the latest token.
 *
 * @param user - Firebase user object
 */
export async function refreshUserToken(user: User): Promise<void> {
  console.info('[AuthService] Refreshing user token...');
  await user.getIdToken(true); // Force refresh
  console.info('[AuthService] Token refreshed');
}

/**
 * Call the backend API to set custom claims
 *
 * After Firebase authentication, this endpoint sets the user's
 * role and organisation claims on their Firebase token.
 *
 * @param user - Firebase user object
 * @throws UserNotAuthorizedError if user is not provisioned (403)
 * @throws Error if the API call fails for other reasons
 */
export class UserNotAuthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserNotAuthorizedError';
  }
}

export async function callSetClaimsEndpoint(user: User): Promise<void> {
  console.info('[AuthService] Calling set-claims endpoint...');
  
  const token = await user.getIdToken();
  const apiUrl = import.meta.env.VITE_API_URL;
  
  if (!apiUrl) {
    throw new Error('VITE_API_URL environment variable is not set');
  }
  
  const response = await fetch(`${apiUrl}/v1/auth/set-claims`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('[AuthService] Set-claims failed:', response.status, errorText);
    
    // 403 = user authenticated but not authorized (not provisioned in system)
    if (response.status === 403) {
      throw new UserNotAuthorizedError(
        'You are not authorized to access this system. Please contact an administrator.'
      );
    }
    
    // 404 = user or organisation not found
    if (response.status === 404) {
      throw new UserNotAuthorizedError(
        'Your account is not set up. Please contact an administrator.'
      );
    }
    
    throw new Error(`Failed to set claims: ${response.statusText}`);
  }
  
  console.info('[AuthService] Custom claims set successfully');
}

/**
 * Check if a user has custom claims set
 *
 * Useful for determining if the user needs to complete
 * the claims setup flow.
 *
 * @param user - Firebase user object
 * @returns True if the user has a role claim
 */
export async function hasCustomClaims(user: User): Promise<boolean> {
  const claims = await getUserClaims(user);
  return !!claims.role;
}
